import React, { useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { WorkTimeTypeSelect } from "./workTimeTypeSelect";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import {
    fetchWorkTimeTypes,
    saveStartWorkTime,
} from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { useTranslation } from "react-i18next";
import { handleApiError, IApiError } from "../../services/baseService";
import { WorkTimeDetails } from "./workTimeDetails";
import { useSelectCurrentWorkShiftTimeSlot } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { useIsWaitingForPlannedShift } from "../../hooks/worktime/useSelectWorkShifts";
import { AlertSeverity } from "../../models/common/enums";
import { Divider, IconButton } from "@mui/material";
import { getGeolocationPosition } from "../../framework/common/geolocation";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    AlertType,
    WorkTimeTypeDrawerState,
    addAlert,
    deleteAlertByType,
    toggleWorkTimeTypeDrawer,
} from "../../framework/redux/slices/appSlice";
import { fetchCurrentWorkShift } from "../../framework/redux/slices/workShiftSlice";
import { getWorkShiftTimeSlotDurationMinutes, WorkTimeTypeType } from "../../models/workShiftTimeSlot/workShiftTimeSlot";
import { workTimeAutomationAlertDuration } from "../../hooks/worktime/useListenWorkTimeMessages";
import { saveWorkTimeLocation, WorkShiftTimeSlotStartResult } from "../../services/workShiftTimeSlotService";
import { PushPin, PushPinOutlined } from "@mui/icons-material";
import { generatePath, useNavigate } from "react-router-dom";
import { addSuccessMsg } from "../../framework/redux/slices/appSlice";
import { store } from "../../framework/redux/store";
import { useWorkTimeDefaultTimezone } from "../../hooks/framework/useWorkTimeDefaultTimezone";
import { AppUtils } from "../../models/common/appUtils";
import { useInterval } from "usehooks-ts";

const drawerBleeding = 52 as const;

export const workTimeGeolocationPositionErrorDuration = 6000 as const;

const StyledBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.dark,
}));

const Puller = styled(Box)(({ theme }) => ({
    width: 30,
    height: 6,
    backgroundColor: theme.palette.white.main,
    borderRadius: 3,
    position: "absolute",
    top: 8,
    left: "calc(50% - 15px)",
}));

// Swiping from bottom doesn't work great on iOS, so we modify behavior for it.
const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

export default function WorkTimeTypeDrawer() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [open, setOpen] = useState(false);
    const [isChangeTaskAction, setIsChangeTaskAction] = useState(false);
    const [isTabledDevice, setIsTabledDevice] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [workTimeTypeId, setWorkTimeTypeId] = useState<string>();
    const current = useSelectCurrentWorkShiftTimeSlot();
    const [windowDimensions, setWindowDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [defaultTz] = useWorkTimeDefaultTimezone();

    //Menu pinned -setting Local storage
    const storageKey = "shiftTaskMenuPinned";
    const defaultShiftTaskMenuPinned = false;
    //Retrieve saved pinned setting
    const savedTaskMenuPinned = localStorage.getItem(storageKey);
    const workTaskMenuPinned = savedTaskMenuPinned ? JSON.parse(savedTaskMenuPinned) : defaultShiftTaskMenuPinned;
    // Current ongoing worktime type duration
    const [workHours, setWorkHours] = useState<number>();

    const updateWorkHours = useCallback(
        () => {
            if (current?.startDate) setWorkHours(getWorkShiftTimeSlotDurationMinutes(current));
        },
        [current]
    );

    useEffect(() => {
        updateWorkHours();
    }, [updateWorkHours]);

    useInterval(updateWorkHours, current ? 30000 : null);

    useEffect(() => {
        const handleResize = () => {
            setWindowDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (isTabledDevice) {
            if (workTaskMenuPinned === true) setOpen(true);
        }
    }, [workTaskMenuPinned, isTabledDevice]);

    useEffect(() => {
        if (windowDimensions?.width >= 720 || windowDimensions.height >= 700) {
            setIsTabledDevice(true);
        } else {
            setIsTabledDevice(false);
        }
    }, [windowDimensions]);

    const usesLocationData = useAppSelector(
        ({ session }) => session.employeeGroupParameters?.usesLocationData
    );
    const workTimeTypes = useAppSelector(
        ({ workShiftTimeSlot }) => workShiftTimeSlot.workTimeTypes
    );
    const formItem = useAppSelector(
        ({ workShiftTimeSlot }) => workShiftTimeSlot.formItem
    );
    const drawerState = useAppSelector(
        ({ app }) => app.drawer
    );

    const isWaitingForPlannedShift = useIsWaitingForPlannedShift();

    useEffect(() => {
        const promise = dispatch(fetchWorkTimeTypes());
        return () => {
            promise.abort();
        };
    }, []);

    useEffect(() => {
        if (formItem) {
            setOpen(true);
            // Also valid when starting new work shift, user should be able to
            // start shift without task
            setIsChangeTaskAction(true);
        } else {
            setIsChangeTaskAction(false);
        }
    }, [formItem]);

    useEffect(() => {
        if (drawerState !== WorkTimeTypeDrawerState.None) {
            // Set drawer open/closed and reset pending state to None.

            if (drawerState === WorkTimeTypeDrawerState.PendingOpen && current?.workTimeTypeType === WorkTimeTypeType.Driving) {
                setOpen(true);
                dispatch(
                    addAlert({
                        message: t("workTime.vehicleStopped"),
                        severity: AlertSeverity.Info,
                        duration: workTimeAutomationAlertDuration,
                        type: AlertType.VehicleStopped,
                    })
                );
            } else if (drawerState === WorkTimeTypeDrawerState.PendingClose &&
                (!isTabledDevice || (isTabledDevice && !workTaskMenuPinned))
            ) {
                setOpen(false);
            }

            dispatch(toggleWorkTimeTypeDrawer(WorkTimeTypeDrawerState.None));
        }
    }, [drawerState]);

    useEffect(() => {
        setWorkTimeTypeId(current?.workTimeTypeId);
    }, [current?.workTimeTypeId]);

    const toggleDrawer = (newOpen: boolean) => () => {
        setOpen(newOpen);
    };

    const toggleMenuPin = () => {
        localStorage.setItem(storageKey, JSON.stringify(!workTaskMenuPinned));
    };

    const handleStartAction = () => {
        const selectedWorkTimeType = workTimeTypes.find(i => i.workTimeTypeId === workTimeTypeId);
        if (selectedWorkTimeType?.isCommentInUse) {
            navigate(generatePath("/work/workstage/definition/:id",
                {
                    id: selectedWorkTimeType.workTimeTypeId,
                }
            ));
        } else {
            void handleSave();
        }
    };

    const handleSave = async() => {
        setSubmitting(true);

        const { costCenterId, vehicleId, salaryRowTypeId, customerId } =
            formItem || current || {};

        await dispatch(
            saveStartWorkTime({
                costCenterId,
                workTimeTypeId,
                timezoneName: defaultTz,
                vehicleId,
                salaryRowTypeId,
                customerId,
            })
        )
            .unwrap()
            .then(async(result: WorkShiftTimeSlotStartResult) => {
                if (usesLocationData) {
                    getGeolocationPosition({
                        message: t("workTime.geolocationPositionError"),
                        severity: AlertSeverity.Warning,
                        duration: workTimeGeolocationPositionErrorDuration,
                    })
                        .then((location) => {
                            if (!!location)
                                return Promise.all([
                                    saveWorkTimeLocation(
                                        result.newSlot.id,
                                        location,
                                        "start"
                                    ),
                                    result.finishedSlotId
                                        ? saveWorkTimeLocation(
                                            result.finishedSlotId,
                                            location,
                                            "finish"
                                        )
                                        : Promise.resolve(),
                                ]);
                        })
                        .catch(console.error);
                }
                await dispatch(fetchCurrentWorkShift());
                const updatedShift = store.getState().workShift.current;

                if (
                    updatedShift?.startTimePlannedWorkShiftId &&
                    new Date() < new Date(updatedShift.startTime)
                ) {
                    dispatch(
                        addSuccessMsg(
                            t("workTime.waitingForPlannedShift"),
                            5000
                        )
                    );
                }
                if (
                    !isTabledDevice ||
                    (isTabledDevice && !workTaskMenuPinned)
                ) {
                    setOpen(false);
                }
                setIsChangeTaskAction(false);
            })
            .catch((e: IApiError) => {
                if (e?.code === "409") {
                    dispatch(
                        addAlert({
                            severity: AlertSeverity.Warning,
                            message: t("workTime.workTimeStartConflict"),
                            duration: 5000,
                        })
                    );
                } else {
                    handleApiError(e);
                }

                // Load current shift just in case.
                setTimeout(() => void dispatch(fetchCurrentWorkShift()), 3000);
            })
            .finally(() => {
                dispatch(deleteAlertByType(AlertType.VehicleStopped));
                setSubmitting(false);
            });
    };

    if (!workTimeTypes) {
        // Render drawer only after data is fetched to fix layout shifting
        return null;
    }

    return (
        <SwipeableDrawer
            anchor="bottom"
            open={open}
            onClose={toggleDrawer(false)}
            onOpen={toggleDrawer(true)}
            variant={isTabledDevice ? "persistent" : "temporary"}
            swipeAreaWidth={drawerBleeding}
            disableDiscovery={isIos}
            disableSwipeToOpen={isIos}
            ModalProps={{
                keepMounted: true,
            }}
            allowSwipeInChildren={true}
            PaperProps={{
                sx: {
                    overflow: "visible",
                    maxHeight: `calc(100% - ${drawerBleeding}px)`,
                },
            }}
        >
            <StyledBox
                onClick={() => {
                    toggleDrawer(!open)();
                }}
                sx={{
                    position: "absolute",
                    top: -drawerBleeding,
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    visibility: "visible",
                    right: 0,
                    left: 0,
                    height: drawerBleeding + 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}
            >
                {!isIos && <Puller />}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={isWaitingForPlannedShift
                            ? { color: theme => theme.palette.warning.dark, ml: 2 }
                            : { color: "success.light", ml: 2 }}
                    >
                        {isWaitingForPlannedShift
                            ? t("workTime.waitingForPlannedShiftShort")
                            : current?.workTimeTypeName ||
                            (current ? t("workTime.atWork") : t("workTime.notAtWork"))}
                    </Typography>

                    {current && (
                        <Typography
                            variant="h5"
                            sx={{
                                color: theme => theme.palette.text.secondary, ml: 2
                            }}
                        >
                            {AppUtils.getDurationStrByDurationMin(workHours, true)}
                        </Typography>
                    )}
                </Box>

                {isIos && (
                    <Box mr={2}>
                        {!open && <ExpandLessIcon color="white"/>}
                        {open && <ExpandMoreIcon color="white"/>}
                    </Box>
                )}

                {(isTabledDevice && open) &&
                <Box>
                    <IconButton
                        size="large"
                        edge="start"
                        aria-label="pin-task-menu"
                        sx={{ color: workTaskMenuPinned ? "success.light" : "LightGray" }}
                        onClick={toggleMenuPin}
                    >
                        {workTaskMenuPinned ? <PushPin /> : <PushPinOutlined />}
                    </IconButton>
                </Box>
                }
            </StyledBox>

            <Divider sx={{ borderColor: "gray.main", zIndex: 1 }} />

            <StyledBox>
                <WorkTimeDetails current={current} />
                <WorkTimeTypeSelect
                    workTimeTypes={workTimeTypes}
                    actionButtons={[
                        {
                            key: "save",
                            color: "primary",
                            variant: "contained",
                            onClick: () => void handleStartAction(),
                            children: !current
                                ? t("workTime.startWork")
                                : t("common.confirm"),
                            loading: submitting,
                        },
                    ]}
                    onChange={(value) => setWorkTimeTypeId(value)}
                    value={workTimeTypeId || null}
                    currentValue={current?.workTimeTypeId}
                    disabled={submitting}
                    isChangeTaskAction={isChangeTaskAction}
                />
            </StyledBox>
        </SwipeableDrawer>
    );
}
